import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  COMPENSATION_MODES,
  computeCompensationMix,
  computeRecurringNonRecurringPercentages,
  computeRevenueMixByLine,
  isMultiLineValuationEnabled,
} from '../utils/revenueUtils';
import IncomeYieldOnAssetsCard from './IncomeYieldOnAssetsCard';

const CHARTS_I18N = 'succession.profile_drawer.profile_charts';

// Mirrors `featureList()` in revenueUtils.js — accept both the normalized
// `feature_types` array and the legacy `features` shape (array or hash).
const companyHasFeature = (company, flag) => {
  if (!company) return false;
  const ft = company.feature_types;
  if (Array.isArray(ft) && ft.map(String).includes(flag)) return true;
  const f = company.features;
  if (Array.isArray(f) && f.map(String).includes(flag)) return true;
  if (f && typeof f === 'object' && !Array.isArray(f) && f[flag] === true) return true;
  return false;
};

const ProfileCharts = ({ user, company }) => {
  const intl = useIntl();
  const { recurring: percentRecurring, nonRecurring: percentNonRecurring } =
    computeRecurringNonRecurringPercentages({ user, company });
  
  const percentFeeBased = user.percent_fee_based || 0;
  const percentCommission = user.percent_commission || (1 - percentFeeBased);

  const compensationMix = computeCompensationMix({ user, company });

  // Get marketplace colors from CSS variables
  const [marketplaceColors, setMarketplaceColors] = useState({
    primary: '#3b82f6',
    secondary: '#10b981',
    accent: '#8b5cf6',
    neutral: '#f59e0b',
  });

  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    
    setMarketplaceColors({
      primary: computedStyle.getPropertyValue('--succession-primary').trim() || '#3b82f6',
      secondary: computedStyle.getPropertyValue('--succession-secondary').trim() || '#10b981',
      accent: computedStyle.getPropertyValue('--succession-accent').trim() || '#8b5cf6',
      neutral: computedStyle.getPropertyValue('--succession-neutral').trim() || '#f59e0b',
    });
  }, []);

  // Donut chart component
  const DonutChart = ({
    percent1,
    percent2,
    label1,
    label2,
    color1,
    color2,
    centerLabel,
  }) => {
    const size = 200;
    const strokeWidth = 24;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    
    // Every percentage reaching this chart is a 0–1 fraction (User#calculate_percent_*),
    // so scale unconditionally. A previous `if (pct < 1)` guard skipped an exact
    // 1.0 and rendered a 100% advisor as "1%".
    const toPct = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return 0;
      return Math.round(Math.min(100, Math.max(0, n * 100)));
    };
    const pct1 = toPct(percent1);
    const pct2 = toPct(percent2);

    // Add small gap between segments to prevent visual artifacts. Arcs are clamped
    // at 0 because a negative stroke-dasharray is invalid — the browser discards the
    // dash pattern and strokes the entire circle, letting a 0% slice paint a full ring.
    const gapSize = 2;
    const arcFor = (pct) => Math.max(0, (pct / 100) * circumference - gapSize);
    const dash1 = arcFor(pct1);
    const dash2 = arcFor(pct2);
    const offset2 = pct1 > 0 ? -(dash1 + gapSize) : 0;
    
    return (
      <div className="flex flex-col items-center gap-4">
        <div className="relative" style={{ width: size, height: size }}>
          <svg viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
            {/* Background circle - drawn first */}
            <circle 
              cx={size / 2} 
              cy={size / 2} 
              r={radius} 
              fill="none" 
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
            />
            {/* First segment — omitted entirely at 0% */}
            {dash1 > 0 && (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color1}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dash1} ${Math.max(0, circumference - dash1)}`}
                strokeLinecap="butt"
              />
            )}
            {/* Second segment — omitted entirely at 0% */}
            {dash2 > 0 && (
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color2}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dash2} ${Math.max(0, circumference - dash2)}`}
                strokeDashoffset={offset2}
                strokeLinecap="butt"
              />
            )}
          </svg>
          {/* Center label + percentage */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              {centerLabel && (
                <div className="text-xs font-medium text-succession-text-muted">
                  {centerLabel}
                </div>
              )}
              <div className="text-2xl font-bold text-succession-text-primary">
                {pct1}%
              </div>
            </div>
          </div>
        </div>
        {/* Legend */}
        <div className="flex flex-col gap-2 text-sm w-full">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color1 }}
              />
              <span className="text-succession-text-muted">{label1}</span>
            </div>
            <span className="text-succession-text-primary font-medium">{pct1}%</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: color2 }}
              />
              <span className="text-succession-text-muted">{label2}</span>
            </div>
            <span className="text-succession-text-primary font-medium">{pct2}%</span>
          </div>
        </div>
      </div>
    );
  };

  // Multi-segment donut for Revenue Mix (by line of business)
  const MultiDonut = ({ data, title }) => {
    if (!data || data.length === 0) return null;
    const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
    if (total <= 0) return null;

    const sorted = [...data].sort((a, b) => (b.value || 0) - (a.value || 0));
    const dominant = sorted[0];
    const dominantPct = dominant ? Math.round(((dominant.value || 0) / total) * 100) : 0;

    const size = 200;
    const strokeWidth = 24;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const gapSize = 2; // match other donuts: small gap between segments
    const colors = [
      marketplaceColors.primary,
      marketplaceColors.secondary,
      marketplaceColors.accent,
      marketplaceColors.neutral,
    ];
    // An item may pin its own colour so it stays stable across advisors instead
    // of shifting with its position in a filtered list.
    const colorFor = (item, i) => item.color || colors[i % colors.length];

    let offset = 0;

    return (
      <div className="space-y-4">
        <h4 className="text-base font-medium text-succession-text-primary text-center">
          {title}
        </h4>
        <div className="flex flex-col items-center gap-4">
          <div className="relative" style={{ width: size, height: size }}>
            <svg viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
              {/* No background ring: data sums to 100%, and gaps should read as the card background */}
              {data.map((item, i) => {
                const pct = (item.value / total) * 100;
                const dashRaw = (pct / 100) * circumference;
                const dash = Math.max(0, dashRaw - gapSize);
                const el = (
                  <circle
                    key={item.id || i}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={colorFor(item, i)}
                    strokeWidth={strokeWidth}
                    strokeDasharray={`${dash} ${Math.max(0, circumference - dash)}`}
                    strokeDashoffset={offset}
                    strokeLinecap="butt"
                  />
                );
                offset -= (dash + gapSize);
                return el;
              })}
            </svg>
            {/* Center dominant line-of-business */}
            {dominant && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-4">
                  <div className="text-xs font-medium text-succession-text-muted truncate">
                    {dominant.label}
                  </div>
                  <div className="text-2xl font-bold text-succession-text-primary">
                    {dominantPct}%
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 text-sm w-full">
            {data.map((item, i) => {
              const pct = Math.round((item.value / total) * 100);
              return (
                <div key={item.id || i} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colorFor(item, i) }} />
                    <span className="text-succession-text-muted">{item.label}</span>
                  </div>
                  <span className="text-succession-text-primary font-medium">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const revenueMixData = computeRevenueMixByLine({ user, company });

  const compensationTitle = intl.formatMessage({ id: `${CHARTS_I18N}.compensation_mix` });

  // Three outcomes: a per-stream breakdown from real revenue components, the
  // stored fee-vs-commission split, or nothing trustworthy to chart. See
  // computeCompensationMix for why the third case must not fall back.
  const compensationChartFor = (mode) => {
    if (mode === COMPENSATION_MODES.STREAMS) {
      // MultiDonut renders its own heading, so no wrapper <h4> here.
      return (
        <MultiDonut
          data={compensationMix.components.map((component) => ({
            ...component,
            label: intl.formatMessage({ id: `${CHARTS_I18N}.${component.labelKey}` }),
            color: marketplaceColors[component.colorToken],
          }))}
          title={compensationTitle}
        />
      );
    }

    return (
      <div className="space-y-4">
        <h4 className="text-base font-medium text-succession-text-primary text-center">
          {compensationTitle}
        </h4>
        {mode === COMPENSATION_MODES.UNAVAILABLE ? (
          <p className="text-sm text-succession-text-muted text-center py-8">
            {intl.formatMessage({ id: `${CHARTS_I18N}.compensation_unavailable` })}
          </p>
        ) : (
          <DonutChart
            percent1={percentFeeBased}
            percent2={percentCommission}
            label1={intl.formatMessage({ id: `${CHARTS_I18N}.fee_based` })}
            label2={intl.formatMessage({ id: `${CHARTS_I18N}.commission_based` })}
            color1={marketplaceColors.accent}
            color2={marketplaceColors.neutral}
            centerLabel={intl.formatMessage({ id: `${CHARTS_I18N}.fee_based_label` })}
          />
        )}
      </div>
    );
  };

  const compensationChart = compensationChartFor(compensationMix.mode);

  return (
    <div>
      <h3 className="text-lg font-semibold text-succession-text-primary mb-4">
        {intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.revenue_breakdown' })}
      </h3>
      <div className={`grid grid-cols-1 ${isMultiLineValuationEnabled(company) ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8 py-6`}>
        {/* Gross Production Chart */}
        <div className="space-y-4">
          <h4 className="text-base font-medium text-succession-text-primary text-center">
            {intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.revenue_mix' })}
          </h4>
          <DonutChart
            percent1={percentRecurring}
            percent2={percentNonRecurring}
            label1={intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.recurring_revenue' })}
            label2={intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.non_recurring_revenue' })}
            color1={marketplaceColors.primary}
            color2={marketplaceColors.secondary}
            centerLabel={intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.recurring_label' })}
          />
        </div>

        {/* Compensation Mix Chart — swapped for Income Yield on Assets when
            the feature flag is enabled. Same column, same spacing. */}
        {companyHasFeature(company, 'income_yield_on_assets_chart') ? (
          <IncomeYieldOnAssetsCard user={user} company={company} />
        ) : (
          compensationChart
        )}

        {/* Revenue Mix (by line of business) */}
        {isMultiLineValuationEnabled(company) && (
          <MultiDonut data={revenueMixData} title={intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.product_mix' })} />
        )}
      </div>
    </div>
  );
};

ProfileCharts.propTypes = {
  user: PropTypes.shape({
    percent_revenue_recurring: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    percent_non_recurring_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    percent_fee_based: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    percent_commission: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fee_based_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    trailer_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    transactional_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    direct_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  company: PropTypes.shape({
    feature_types: PropTypes.arrayOf(PropTypes.string),
    features: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.object]),
  }),
};

export default ProfileCharts;

